import React, { useCallback, useContext, useMemo, useState } from 'react'

import { getTranslate } from 'react-localize-redux'
import { getUser } from '../../../../reducers/UserReducer'
import { connect } from 'react-redux'
import SectorsTable from './SectorsTable'
import useRequest from '../../../../hooks/useRequest'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { getInstitutionSectors } from '../../../../utils/api/institution'
import { archiveSector, deleteSector } from '../../../../utils/api/sector'
import { InstitutionOptions, OrderBy, OrderDirections } from '../../../../utils/constants'
import SectorModal from '../../../../Providers/Forms/SectorModal'
import FilesManager from '../../../../Components/shared/FilesManager'
import SectorSectionUsers from '../../../../Components/shared/SectorSectionUsers'
import roles from '../../../../utils/roles'

/** @enum {string} */
export const SectorActions = Object.freeze({
  MANAGE_FILES: 'MANAGE_FILES',
  DISPLAY_AFFILIATED_USERS: 'DISPLAY_AFFILIATED_USERS'
})

const DEFAULT_STATE = { data: null, action: null }

const mapStateToProps = state => ({ t: getTranslate(state.locale), user: getUser(state.getUser) })

const SectorsListView = ({ user, displayManagePresets, t }) => {
  const { actTypes, institution, institutionOptions, users } = useContext(InstitutionContext)

  const [parameters, setParameters] = useState(new TableParameters({ pageSize: 50, order: { by: OrderBy.NAME, direction: OrderDirections.ASC } }))
  const [selectedSector, setSelectedSector] = useState(DEFAULT_STATE)
  const { data, loading, metadata, reload } = useRequest({
    request: getInstitutionSectors,
    parameters: useMemo(() => [institution, { ...parameters.toQueryParametersOld(), archived: false, contexts: ['document'] }], [institution, parameters])
  })

  const globalActTypes = useMemo(() => actTypes.filter(a => a.school === null), [actTypes])
  const isNurse = useMemo(() => roles.isNurse(user), [user])
  const isReadOnly = useMemo(() => roles.isObserver(user), [user])
  const isSectorSheetReadOnly = useMemo(() => (
    isReadOnly ||
    ((isNurse && institutionOptions.some(io => io.optionType.type === InstitutionOptions.DISABLE_SECTOR_EDITION_BY_NURSES && io.optionEnabled)))
  ), [institutionOptions, isReadOnly, isNurse])

  const handleArchive = useCallback((sector, status) => {
    archiveSector(user, sector, status).then(reload)
  }, [user, reload])

  const handleDelete = useCallback(sector => deleteSector(user, sector), [user])

  const sectors = useMemo(() => data.map(s => ({ ...s, institution })), [institution, data])

  const resetState = useCallback(() => setSelectedSector(DEFAULT_STATE), [setSelectedSector])

  return (
    <>
      <SectorModal actTypes={globalActTypes} users={users} readOnly={isSectorSheetReadOnly}>
        <SectorsTable
          data={sectors}
          disabledAdd={isNurse || isReadOnly}
          institution={institution}
          loading={loading}
          metadata={metadata}
          parameters={parameters}
          readOnly={isReadOnly}
          displayManagePresets={displayManagePresets}
          onArchive={s => handleArchive(s, true)}
          onAction={setSelectedSector}
          onDelete={handleDelete}
          onParametersChange={setParameters}
          onUnarchive={s => handleArchive(s, false)}
          onReload={reload}
        />
      </SectorModal>
      <FilesManager
        entity={selectedSector.action === SectorActions.MANAGE_FILES ? selectedSector.data : null}
        entityName='Sector'
        onClose={resetState}
      />
      {selectedSector.action === SectorActions.DISPLAY_AFFILIATED_USERS && (
        <SectorSectionUsers
          disableAddButton={isReadOnly}
          entity={selectedSector.data}
          onClose={resetState}
        />
      )}
    </>
  )
}

export default connect(mapStateToProps)(SectorsListView)
